import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { filterChannelMembersByQuery } from '../utils';
import type { ChatChannelMemberResponse } from '@/lib/api';

/** Minimum typed length before offering an off-list username. */
const FREE_TEXT_MIN_LENGTH = 2;

interface SenderFilterProps {
  /** Loaded channel members — suggestions only, not the limit of what's selectable. */
  members: ChatChannelMemberResponse[];
  /** Currently filtered username, or null for "Anyone". */
  value: string | null;
  onChange: (username: string | null) => void;
}

function memberLabel(member: ChatChannelMemberResponse): string {
  return member.nickname || member.displayName || member.username || member.userId;
}

/**
 * "From" filter for in-channel search — a combobox over channel members that
 * also accepts a typed handle.
 *
 * Deliberately not a plain list of members: `useChatChannel` only loads the
 * first page of members (100), and people who LEFT the channel keep their
 * messages but disappear from the roster. Both would be unreachable in a
 * fixed dropdown, so anything the user types is offered as a filter too — the
 * server resolves the handle and simply returns no matches if it's not real.
 */
export function SenderFilter({ members, value, onChange }: SenderFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectableMembers = useMemo(() => members.filter((m) => !!m.username), [members]);

  const matches = useMemo(
    () => filterChannelMembersByQuery(selectableMembers, search, null),
    [selectableMembers, search],
  );

  const typed = search.trim();
  // Offer the raw text only when it isn't already one of the suggestions —
  // otherwise the same person appears twice in the list.
  const showFreeText =
    typed.length >= FREE_TEXT_MIN_LENGTH &&
    !matches.some((m) => m.username?.toLowerCase() === typed.toLowerCase());

  // A selected handle that isn't in the loaded roster (typed freehand, or a
  // member past the first page) still labels itself — just as the raw username.
  const selectedMember =
    value === null ? null : selectableMembers.find((m) => m.username === value);
  const selectedLabel =
    value === null ? 'Anyone' : selectedMember ? memberLabel(selectedMember) : value;

  const commit = (username: string | null) => {
    onChange(username);
    setSearch('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="From"
          className="flex-1 min-w-0 flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary/40"
        >
          <span className={cn('truncate', value === null && 'text-muted-foreground')}>
            {selectedLabel}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Find someone…"
            value={search}
            onValueChange={setSearch}
            aria-label="Find a sender"
          />
          <CommandList>
            <CommandEmpty>No members match.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__anyone__" onSelect={() => commit(null)}>
                <Check
                  className={cn('mr-2 h-4 w-4', value === null ? 'opacity-100' : 'opacity-0')}
                />
                Anyone
              </CommandItem>
              {matches.map((member) => (
                <CommandItem
                  key={member.userId}
                  value={member.username!}
                  onSelect={() => commit(member.username!)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === member.username ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{memberLabel(member)}</span>
                </CommandItem>
              ))}
              {showFreeText && (
                <CommandItem value={typed} onSelect={() => commit(typed)}>
                  <Check
                    className={cn('mr-2 h-4 w-4', value === typed ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="truncate">Search from “{typed}”</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
